import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList,
} from "recharts";

const BAR_COLORS = ["#0d6efd","#6610f2","#0dcaf0","#198754","#ffc107","#dc3545","#fd7e14","#20c997"];
const BRAND_BLUE = "#0d6efd";
const BRAND_GREEN = "#198754";
const BRAND_ORANGE = "#fd7e14";

const ALL_CHARTS = new Set([
  "kpiTotal", "kpiStores", "kpiDistribution", "kpiStockLevel", "kpiAvgPrice", "kpiMedianPrice",
  "trend", "byBanner", "byItem", "pricing",
]);

export function formatMonthLabel(ym) {
  if (!ym) return "";
  const [year, month] = ym.split("-");
  return new Date(parseInt(year), parseInt(month) - 1).toLocaleString("en", { month: "long" });
}

function SingleLineYTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={4} textAnchor="end" fill="#495057" fontSize={10}>
        {payload.value}
      </text>
    </g>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="col">
      <div className="card text-center h-100">
        <div className="card-body py-3">
          <div className="fs-2 fw-bold text-primary">{value}</div>
          <div className="text-muted small">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardCharts({ data, visibleCharts = ALL_CHARTS }) {
  const show = (key) => visibleCharts.has(key);

  return (
    <>
      {(show("kpiTotal") || show("kpiStores") || show("kpiDistribution") ||
        show("kpiStockLevel") || show("kpiAvgPrice") || show("kpiMedianPrice")) && (
        <div className="row row-cols-2 row-cols-md-3 row-cols-lg-6 g-3 mb-4">
          {show("kpiTotal")        && <KpiCard label="Total Scans"    value={data.kpis.total.toLocaleString()} />}
          {show("kpiStores")       && <KpiCard label="Stores Visited" value={data.kpis.storesVisited.toLocaleString()} />}
          {show("kpiDistribution") && <KpiCard label="Distribution"   value={`${data.kpis.distributionPct}%`} />}
          {show("kpiStockLevel")   && <KpiCard label="Stock Level"    value={`${data.kpis.stockLevelPct}%`} />}
          {show("kpiAvgPrice")     && <KpiCard label="Avg Price"      value={data.kpis.avgPrice === 0 ? "N/A" : `$${data.kpis.avgPrice}`} />}
          {show("kpiMedianPrice")  && <KpiCard label="Median Price"   value={data.kpis.medianPrice === 0 ? "N/A" : `$${data.kpis.medianPrice}`} />}
        </div>
      )}

      {show("trend") && data.monthlyTrend.length > 0 && (
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-center mb-3">Distribution By Month %</h6>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.monthlyTrend.map((p) => ({ ...p, monthLabel: formatMonthLabel(p.month) }))} margin={{ top: 20, right: 16, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                    <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} width={38} hide />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="distributionPct" fill={BRAND_BLUE} name="Distribution %" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="distributionPct" position="insideTop" formatter={(v) => `${v}%`} style={{ fill: "#fff", fontSize: 11, fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-center mb-3">Stock Level By Month %</h6>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.monthlyTrend.map((p) => ({ ...p, monthLabel: formatMonthLabel(p.month) }))} margin={{ top: 20, right: 16, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                    <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} width={38} hide />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Bar dataKey="stockLevelPct" fill={BRAND_GREEN} name="Stock Level %" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="stockLevelPct" position="insideTop" formatter={(v) => `${v}%`} style={{ fill: "#fff", fontSize: 11, fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {(show("byBanner") || show("byItem")) && (
        <div className="row g-3 mb-4">
          {show("byBanner") && (
            <div className={show("byItem") ? "col-md-6" : "col-12"}>
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title mb-3">Distribution by Major Retailer</h6>
                  {data.byMajorBanner.length === 0 ? (
                    <p className="text-muted small">No data.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={data.byMajorBanner} margin={{ top: 4, right: 8, bottom: 50, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                        <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} width={38} />
                        <Tooltip formatter={(v) => `${v}%`} />
                        <Bar dataKey="distributionPct" name="Distribution %" radius={[3, 3, 0, 0]}>
                          {data.byMajorBanner.map((_, i) => (
                            <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          )}
          {show("byItem") && (
            <div className={show("byBanner") ? "col-md-6" : "col-12"}>
              <div className="card h-100">
                <div className="card-body">
                  <h6 className="card-title mb-3">Distribution by Item</h6>
                  {data.byItem.length === 0 ? (
                    <p className="text-muted small">No data.</p>
                  ) : (() => {
                    const labelWidth = Math.min(
                      Math.max(...data.byItem.map((d) => d.name.length)) * 6.5,
                      280
                    );
                    return (
                      <ResponsiveContainer width="100%" height={Math.max(280, data.byItem.length * 28 + 40)}>
                        <BarChart data={data.byItem} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                          <XAxis type="number" unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" tick={<SingleLineYTick />} width={labelWidth} interval={0} />
                          <Tooltip formatter={(v) => `${v}%`} />
                          <Bar dataKey="distributionPct" fill={BRAND_BLUE} name="Distribution %" radius={[0, 3, 3, 0]}>
                            <LabelList dataKey="distributionPct" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 10, fill: "#495057" }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {show("pricing") && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-title mb-3">Pricing (Mean / Median / Mode)</h6>
            {data.pricing.length === 0 ? (
              <p className="text-muted small">No pricing data for selected filters.</p>
            ) : (() => {
              const labelWidth = Math.min(
                Math.max(...data.pricing.map((d) => d.name.length)) * 6.5,
                280
              );
              return (
                <ResponsiveContainer width="100%" height={Math.max(240, data.pricing.length * 40 + 60)}>
                  <BarChart data={data.pricing} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={<SingleLineYTick />} width={labelWidth} interval={0} />
                    <Tooltip formatter={(v, name) => [`$${v}`, name]} />
                    <Legend verticalAlign="bottom" />
                    <Bar dataKey="mean" fill={BRAND_BLUE} name="Mean" radius={[0, 2, 2, 0]} />
                    <Bar dataKey="median" fill={BRAND_GREEN} name="Median" radius={[0, 2, 2, 0]} />
                    <Bar dataKey="mode" fill={BRAND_ORANGE} name="Mode" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}
